"use client"

import { Avatar, Separator, Text } from "@/elements"
import React, { FC } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { StarIcon } from "@/icons/solid/StarIcon"
import Link from "next/link"
import clsx from "clsx"

const CreativeCreditFragment = graphql(`
  fragment CreativeCredit on CreativeWorkPortfolioProjectContributor {
    __typename
    id
    rolesString
    name
    avatarInitials
    avatar80w: avatarUrl(width: 80)

    ... on CreativeWorkPortfolioProjectCreativeContributor {
      creative {
        id
        __typename
        publicVisibility
        ... on IndividualCreative {
          handle
        }
      }
    }
  }
`)

type CreatorCredit = {
  creatorRolesString: string
  name: string
  avatarInitials: string
  avatar80w: string
  handle: string
}

interface Props {
  creatorCredit?: CreatorCredit
  grid?: boolean
  showSeparator?: boolean
  contributor?: FragmentType<typeof CreativeCreditFragment> | undefined | null
  itemClassName?: string
  wrapperClassName?: string
  openLinkInNewTab?: boolean
}

/*
 * This component handles rendering the credit for a contributor or creator.
 * Pass in the creatorCredit prop to render the creator credit. i.e. the person that
 * published the project. OTHERWISE pass in the contributor prop to render the contributor credit.
 */
export const CreativeCredit: FC<Props> = props => {
  const { creatorCredit, grid = false, showSeparator, wrapperClassName, itemClassName } = props
  const contributor = useFragment(CreativeCreditFragment, props.contributor)

  const isWriteIn = contributor?.__typename === "CreativeWorkPortfolioProjectWriteInContributor"

  const isCreator = Boolean(creatorCredit)

  const creativeWorkPortfolioProjectCreativeContributor =
    contributor?.__typename === "CreativeWorkPortfolioProjectCreativeContributor" ? contributor : null
  const contributorCreative =
    creativeWorkPortfolioProjectCreativeContributor?.creative?.__typename === "IndividualCreative"
      ? creativeWorkPortfolioProjectCreativeContributor?.creative
      : null

  const href = isWriteIn ? "" : isCreator ? `/${creatorCredit?.handle}` : `/${contributorCreative?.handle}`

  // Split the roles into an array
  const roles = (isCreator ? creatorCredit?.creatorRolesString : contributor?.rolesString)?.split(",") || []

  return (
    <div className={clsx("flex w-full flex-col", wrapperClassName)}>
      {roles.map((role, index) => (
        <div className={clsx("w-full", itemClassName)} key={index}>
          <Link
            key={index}
            href={href}
            target={props.openLinkInNewTab ? "_blank" : undefined}
            className={clsx(isWriteIn && "pointer-events-none", "w-full")}>
            <div className={clsx("flex flex-row items-center", grid && "grid w-full grid-cols-12")}>
              <Text className={clsx(grid && "col-span-6")}>{role.trim()}:</Text>
              <div className={clsx(grid ? "col-span-6" : "ml-2")}>
                <div
                  className={clsx(
                    "flex h-[36px] w-fit flex-shrink flex-grow-0 flex-row items-center gap-[6px] rounded-md bg-bg-2 px-[6px] py-1"
                  )}>
                  <Avatar
                    size="2xs"
                    initials={isCreator ? creatorCredit?.avatarInitials : contributor?.avatarInitials}
                    src={isCreator ? creatorCredit?.avatar80w : contributor?.avatar80w}
                  />
                  <Text variant="body2" className="flex flex-shrink-0">
                    {isCreator ? creatorCredit?.name : contributor?.name}
                  </Text>
                  {isCreator && <StarIcon className="fill-primary-3" />}
                </div>
              </div>
            </div>
          </Link>
          {showSeparator && <Separator className="mt-2" />}
        </div>
      ))}
    </div>
  )
}
